import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FaSpotify, FaApple, FaYoutube } from "react-icons/fa";
import { Play, Heart, Share2, Clock, Plus } from "lucide-react";
import { toast } from "sonner";
import { useMusic } from "@/context/MusicPlayer";
import Artwork from "@/components/Artwork";
import NowPlayingBadge from "@/components/NowPlayingBadge";
import { trackPlay, trackPlatformClick } from "@/lib/analytics";
import { TID } from "@/constants/testIds";

const ReleaseCard = ({ release, index = 0, list = null }) => {
  const { play, enqueue, favorites, toggleFavorite, current } = useMusic();
  const isFav = favorites.includes(release.id);
  const isPlaying = current?.id === release.id;

  const share = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const url = `${window.location.origin}/releases/${release.id}`;
    try {
      if (navigator.share) await navigator.share({ title: `${release.title} — KASHiX`, text: release.description, url });
      else {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied to clipboard");
      }
    } catch (_e) { /* share cancelled */ }
  };

  return (
    <motion.article
      data-testid={TID.releaseCard}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, delay: index * 0.06, ease: [0.22, 1, 0.36, 1] }}
      className={`group relative aspect-[3/4] overflow-hidden rounded-2xl border ${isPlaying ? "border-[#FF9933]/50" : "border-white/5"} bg-[#0f0f0f]`}
    >
      <Link to={`/releases/${release.id}`} className="absolute inset-0" aria-label={`Open ${release.title}`}>
        <Artwork
          src={release.cover_image}
          alt={release.title}
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-105"
          rounded=""
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
             style={{ background: 'radial-gradient(60% 40% at 50% 100%, rgba(255,153,51,0.28), transparent 70%)' }} />
      </Link>

      <div className="absolute top-3 left-3 flex items-center gap-2 z-10 flex-wrap">
        {isPlaying ? (
          <NowPlayingBadge />
        ) : (
          <span className="text-[10px] uppercase tracking-[0.3em] text-[#FF9933] bg-black/60 backdrop-blur px-2 py-1 rounded">
            {release.year}
          </span>
        )}
        {release.duration && (
          <span className="inline-flex items-center gap-1 text-[10px] text-white/70 bg-black/60 backdrop-blur px-2 py-1 rounded">
            <Clock size={10} /> {release.duration}
          </span>
        )}
      </div>

      <div className="absolute top-3 right-3 flex items-center gap-1 z-10">
        <button
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleFavorite(release.id); toast.success(isFav ? "Removed from favorites" : "Added to favorites"); }}
          aria-label={isFav ? "Remove favorite" : "Add favorite"}
          data-testid={`favorite-${release.id}`}
          className={`w-8 h-8 rounded-full flex items-center justify-center backdrop-blur bg-black/60 border border-white/10 transition ${isFav ? "text-[#FF9933]" : "text-white/70 hover:text-[#FF9933]"}`}
        >
          <Heart size={13} fill={isFav ? "currentColor" : "none"} />
        </button>
        <button
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); enqueue(release); toast.success("Added to queue"); }}
          aria-label="Add to queue"
          data-testid={`enqueue-${release.id}`}
          className="w-8 h-8 rounded-full flex items-center justify-center backdrop-blur bg-black/60 border border-white/10 text-white/70 hover:text-[#FF9933] transition"
        >
          <Plus size={13} />
        </button>
        <button
          onClick={share}
          aria-label="Share"
          data-testid={`share-${release.id}`}
          className="w-8 h-8 rounded-full flex items-center justify-center backdrop-blur bg-black/60 border border-white/10 text-white/70 hover:text-[#FF9933] transition"
        >
          <Share2 size={12} />
        </button>
      </div>

      <div className="absolute inset-x-0 bottom-0 p-5 z-10 pointer-events-none">
        <div className="font-hindi text-lg text-white/85 pointer-events-auto">{release.hindi_title}</div>
        <Link to={`/releases/${release.id}`} className="pointer-events-auto">
          <h3 className="font-display text-2xl md:text-[26px] text-white leading-tight tracking-tight mt-1 hover:text-[#FF9933] transition">
            {release.title}
          </h3>
        </Link>
        <div className="mt-1 text-[11px] uppercase tracking-[0.3em] text-white/60">{release.genre}</div>

        <div className="mt-4 flex items-center gap-3 pointer-events-auto">
          <button
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); play(release, list); trackPlay(release.id); }}
            data-testid={`play-${release.id}`}
            className="inline-flex items-center gap-2 rounded-full bg-[#FF9933] text-black px-4 py-2 text-xs uppercase tracking-[0.25em] font-semibold hover:bg-[#FFD700] hover:shadow-[0_0_20px_rgba(255,153,51,0.6)] transition-all"
          >
            <Play size={12} fill="currentColor" /> {isPlaying ? "Playing" : "Play"}
          </button>
          <div className="flex items-center gap-3 text-white/75">
            {release.spotify_url && (
              <a onClick={() => trackPlatformClick("spotify", release.id)} href={release.spotify_url} target="_blank" rel="noreferrer noopener" aria-label="Spotify" className="hover:text-[#FF9933]" data-testid={TID.releaseSpotify}>
                <FaSpotify size={16} />
              </a>
            )}
            {release.apple_music_url && (
              <a onClick={() => trackPlatformClick("apple", release.id)} data-testid={TID.releaseApple} href={release.apple_music_url} target="_blank" rel="noreferrer noopener" aria-label="Apple Music" className="hover:text-[#FF9933]">
                <FaApple size={16} />
              </a>
            )}
            {release.youtube_url && (
              <a onClick={() => trackPlatformClick("youtube", release.id)} data-testid={TID.releaseYoutube} href={release.youtube_url} target="_blank" rel="noreferrer noopener" aria-label="YouTube" className="hover:text-[#FF9933]">
                <FaYoutube size={16} />
              </a>
            )}
          </div>
        </div>
      </div>
    </motion.article>
  );
};

export default ReleaseCard;
